import { apiService } from './api'
import { Transaction } from '../types'

export interface DashboardData {
  currentBalance: number
  monthlyIncome: number
  monthlyExpenses: number
  savingsRate: number
  transactionCount: number
  categoryBreakdown: Array<{
    categoryId: number
    categoryName: string
    amount: number
    percentage: number
    color: string
  }>
  monthlyTrends: Array<{
    month: string
    income: number
    expenses: number
    savings: number
  }>
  recentTransactions: Transaction[]
  topCategories: Array<{
    categoryName: string
    amount: number
    transactionCount: number
    color: string
  }>
  spendingByDay: Array<{
    dayOfWeek: string
    averageAmount: number
    transactionCount: number
  }>
}

export interface PeriodSummary {
  totalIncome: number
  totalExpenses: number
  netSavings: number
  transactionCount: number
  averageTransactionAmount: number
  largestExpense: number
  largestIncome: number
  categoryBreakdown: Array<{
    categoryName: string
    amount: number
    percentage: number
  }>
}

export class DashboardService {
  async getDashboard(dateFrom?: string, dateTo?: string): Promise<DashboardData> {
    const params = { dateFrom, dateTo }
    const response = await apiService.get<any>('/dashboard', params)
    
    // Map recent transactions from dashboard response
    const mappedRecentTransactions = (response.recentTransactions || []).map((transaction: any) => ({
      id: transaction.id,
      amount: transaction.amount,
      description: transaction.description,
      type: transaction.type,
      date: transaction.transactionDate,
      transactionDate: transaction.transactionDate,
      categoryName: transaction.category?.name || 'Uncategorized',
      categoryId: transaction.categoryId,
      userId: transaction.userId,
      createdAt: transaction.createdAt,
      updatedAt: transaction.updatedAt
    }))
    
    // Map backend response to frontend interface
    return {
      currentBalance: response.currentBalance || 0,
      monthlyIncome: response.periodSummary?.income || 0,
      monthlyExpenses: response.periodSummary?.expenses || 0,
      savingsRate: response.periodSummary?.netAmount ? 
        (response.periodSummary.netAmount / (response.periodSummary.income || 1)) * 100 : 0,
      transactionCount: response.periodSummary?.totalTransactions || 0,
      categoryBreakdown: (response.categoryBreakdown?.expenses || []).map((cat: any) => ({
        categoryId: cat.categoryId,
        categoryName: cat.categoryName,
        amount: cat.amount,
        percentage: cat.percentage || 0,
        color: cat.color || '#1976d2'
      })),
      monthlyTrends: (response.monthlyTrend || []).map((trend: any) => ({
        month: trend.month,
        income: trend.income,
        expenses: trend.expenses,
        savings: trend.net || (trend.income - trend.expenses)
      })),
      recentTransactions: mappedRecentTransactions,
      topCategories: (response.topCategories?.expenses || []).map((cat: any) => ({
        categoryName: cat.categoryName,
        amount: cat.totalAmount || cat.amount,
        transactionCount: cat.transactionCount,
        color: cat.color || '#1976d2'
      })),
      spendingByDay: [] // This needs to be fetched separately
    }
  }

  async getCurrentBalance(): Promise<{ balance: number; lastUpdated: string }> {
    return apiService.get('/dashboard/balance')
  }

  async getPeriodSummary(dateFrom?: string, dateTo?: string): Promise<PeriodSummary> {
    const params = { dateFrom, dateTo }
    return apiService.get<PeriodSummary>('/dashboard/summary', params)
  }

  async getMonthlyTrends(months = 12): Promise<Array<{
    month: string
    year: number
    income: number
    expenses: number
    savings: number
    transactionCount: number
  }>> {
    return apiService.get('/dashboard/trends', { months })
  }

  async getCategoryBreakdown(
    type?: 'income' | 'expense',
    dateFrom?: string,
    dateTo?: string
  ): Promise<Array<{
    categoryId: number
    categoryName: string
    amount: number
    percentage: number
    transactionCount: number
    color: string
  }>> {
    const params = { type, dateFrom, dateTo }
    return apiService.get('/dashboard/categories', params)
  }

  async getRecentTransactions(limit = 10): Promise<Transaction[]> {
    return apiService.get<Transaction[]>('/dashboard/recent-transactions', { limit })
  }

  async getTopCategories(
    type?: 'income' | 'expense',
    limit = 5,
    dateFrom?: string,
    dateTo?: string
  ): Promise<Array<{
    categoryName: string
    amount: number
    transactionCount: number
    percentage: number
    color: string
  }>> {
    const params = { type, limit, dateFrom, dateTo }
    return apiService.get('/dashboard/top-categories', params)
  }

  async getSpendingByDayOfWeek(dateFrom?: string, dateTo?: string): Promise<Array<{
    dayOfWeek: string
    averageAmount: number
    transactionCount: number
    totalAmount: number
  }>> {
    const params = { dateFrom, dateTo }
    return apiService.get('/dashboard/spending-by-day', params)
  }

  async getGoalsProgress(): Promise<Array<{
    goalName: string
    targetAmount: number
    currentAmount: number
    percentage: number
    daysRemaining: number
  }>> {
    return apiService.get('/dashboard/goals')
  }
}

export const dashboardService = new DashboardService()